/**
 * single-product-new.js — Prime Tex quote form behavior.
 *
 *  - <spn-quantity>        : row quantity stepper (+/- + input)
 *  - <spn-add-to-quote>    : per-row Add-to-Quote button; wraps Shopify's
 *                            cart_add/cart_change flow. Copied 1:1 from
 *                            quick-order-list.js so cart-icon-bubble and
 *                            CartDrawer update the same way as the theme's
 *                            native quick-order-list section.
 *  - initTabsAndScrollspy  : tab bar + IntersectionObserver scroll-spy
 *  - initSwatchLightbox    : swatch click-to-enlarge
 *
 * Depends on the following globals (loaded by theme.liquid):
 *  - routes.cart_add_url / routes.cart_change_url
 *  - fetchConfig() (theme.js)
 *  - PUB_SUB_EVENTS / publish / subscribe (constants.js, pubsub.js)
 */

(() => {
  'use strict';

  const clampQty = (value) => {
    const num = parseInt(value, 10);
    return Number.isFinite(num) && num >= 1 ? num : 1;
  };

  // Theme globals (loaded by theme.js + constants.js before this script).
  // Fall back to local implementations if not available.
  const debounceFn = typeof window.debounce === 'function'
    ? window.debounce
    : (fn, wait) => {
        let timeout;
        return function () {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(fn.bind(this, ...arguments), wait);
        };
      };
  const ADD_DEBOUNCE_MS = typeof window.ON_CHANGE_DEBOUNCE_TIMER === 'number'
    ? window.ON_CHANGE_DEBOUNCE_TIMER
    : 300;

  // One shared aria-live region for cart-add announcements — screen readers
  // read this without visually rendering anything. Created lazily on first use.
  const getAriaLiveRegion = () => {
    let region = document.getElementById('spn-cart-live-region');
    if (region) return region;
    region = document.createElement('div');
    region.id = 'spn-cart-live-region';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  };
  const announce = (msg) => {
    const region = getAriaLiveRegion();
    // Clear then set so screen readers pick up repeated identical messages.
    region.textContent = '';
    window.setTimeout(() => { region.textContent = msg; }, 20);
  };

  // ---------- Tabs + scroll-spy ----------

  const initTabsAndScrollspy = (root) => {
    const tabs = root.querySelectorAll('[data-spn-tab]');
    const tabsBar = root.querySelector('[data-spn-tabs]');
    const groups = root.querySelectorAll('[data-spn-group]');
    if (!tabs.length || !groups.length || !tabsBar) return;

    const getStickyOffset = () => {
      const headerHeight =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        ) || 0;
      // Below 767px the tabs bar switches to position: static (see
      // primetex.css), so it scrolls away with the page instead of staying
      // pinned — only the header still eats into the visible viewport.
      const tabsBarIsSticky = getComputedStyle(tabsBar).position === 'sticky';
      return headerHeight + (tabsBarIsSticky ? tabsBar.offsetHeight : 0);
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetId = tab.getAttribute('data-target');
        const target = document.getElementById(targetId);
        if (!target) return;
        // Highlight the clicked tab immediately instead of waiting for the
        // IntersectionObserver to catch up with the smooth-scroll animation.
        setActiveTab(targetId);
        const top =
          target.getBoundingClientRect().top +
          window.pageYOffset -
          getStickyOffset() -
          16;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });

    const setActiveTab = (id) => {
      tabs.forEach((tab) => {
        const isActive = tab.getAttribute('data-target') === id;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    if (!('IntersectionObserver' in window)) return;

    let observer;

    // Recompute from real positions rather than trusting whichever entry
    // fired — IntersectionObserver doesn't guarantee entry order, so when two
    // adjacent groups both momentarily overlap the band (one ending, the
    // next starting) the callback could apply them in either order and land
    // on the wrong tab. Scanning every group and keeping the last (lowest)
    // one whose box still overlaps the band under the sticky bar removes
    // that ambiguity — matches the -70% bottom rootMargin below.
    const updateActiveFromPositions = (offset) => {
      // The click handler parks a target's top at offset + 16 (see the click
      // listener above), so "passed the sticky line" must use that same +16
      // gap here — otherwise a freshly-clicked tab's target can sit just
      // below the cutoff and the previously active tab never lets go.
      // bandBottom is derived from passLine (not raw innerHeight) so a tall,
      // wrapped tabs bar — which inflates offset — can't push passLine past
      // bandBottom and starve the band of any match.
      const passLine = offset + 16;
      const bandBottom = passLine + (window.innerHeight - passLine) * 0.3;
      let activeId = null;
      groups.forEach((group) => {
        const rect = group.getBoundingClientRect();
        if (rect.top < bandBottom && rect.bottom > passLine) activeId = group.id;
      });
      if (!activeId) {
        // Band is empty (gap between sections, or before/after the list) —
        // fall back to the last group whose top already passed the sticky line.
        groups.forEach((group) => {
          if (group.getBoundingClientRect().top <= passLine) activeId = group.id;
        });
      }
      setActiveTab(activeId || groups[0]?.id);
    };

    const createObserver = () => {
      if (observer) observer.disconnect();
      const offset = Math.round(getStickyOffset()) + 1;
      observer = new IntersectionObserver(
        () => updateActiveFromPositions(offset),
        { rootMargin: `-${offset}px 0px -70% 0px`, threshold: 0 }
      );
      groups.forEach((group) => observer.observe(group));
    };

    createObserver();
    window.addEventListener('resize', debounceFn(createObserver, 200));
  };

  // ---------- Quantity stepper ----------

  if (!customElements.get('spn-quantity')) {
    customElements.define(
      'spn-quantity',
      class extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('[data-spn-qty-input]');
          const decrease = this.querySelector('[data-spn-qty-decrease]');
          const increase = this.querySelector('[data-spn-qty-increase]');
          if (!this.input) return;

          decrease?.addEventListener('click', () => this.setValue(this.currentValue - 1));
          increase?.addEventListener('click', () => this.setValue(this.currentValue + 1));
          this.input.addEventListener('change', () => this.setValue(this.input.value));
        }

        get currentValue() {
          return parseInt(this.input.value, 10) || 1;
        }

        setValue(next) {
          this.input.value = clampQty(next);
        }
      }
    );
  }

  // ---------- Add to Quote (cart add/update) ----------
  //
  // Mirrors quick-order-list.js:updateQuantity exactly:
  //   - action = 'ADD'    -> POST /cart/add.js with items[].
  //   - action = 'UPDATE' -> POST /cart/change.js with { id, quantity }.
  //   - Re-render cart-icon-bubble + CartDrawer sections from the response.
  //   - Publish PUB_SUB_EVENTS.cartUpdate so other components update.

  if (!customElements.get('spn-add-to-quote')) {
    customElements.define(
      'spn-add-to-quote',
      class extends HTMLElement {
        constructor() {
          super();
          this.actions = { add: 'ADD', update: 'UPDATE' };
        }

        connectedCallback() {
          this.button = this.querySelector('button');
          this.row = this.closest('.spn__row');
          this.variantId = this.getAttribute('data-variant-id');
          if (!this.button || !this.row || !this.variantId) return;

          this.qtyInput = this.row.querySelector('[data-spn-qty-input]');
          this.isRequesting = false;

          // Debounce the actual submit so rapid clicks collapse into one request.
          // Guard with isRequesting so a click during an in-flight request is a no-op.
          this.debouncedSubmit = debounceFn(() => this.submit(), ADD_DEBOUNCE_MS);

          this.button.addEventListener('click', (e) => this.onClick(e));
        }

        onClick(event) {
          event?.preventDefault();
          if (this.isRequesting) return;
          // Mark the pending state immediately so the UI reflects the click.
          this.button.setAttribute('aria-busy', 'true');
          this.button.classList.add('is-pending');
          this.debouncedSubmit();
        }

        submit() {
          if (this.isRequesting) return;
          const inputValue = clampQty(this.qtyInput?.value);
          // Always ADD — each click increments the cart by the input value.
          this.updateQuantity(this.variantId, inputValue, this.actions.add);
        }

        getSectionsToRender() {
          return [
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
            {
              id: 'CartDrawer',
              section: 'cart-drawer',
              selector: '#CartDrawer',
            },
          ];
        }

        getSectionsUrl() {
          return `${window.location.pathname}`;
        }

        // Copy of quick-order-list.js:updateQuantity — same route, body, headers.
        updateQuantity(id, quantity, action) {
          this.isRequesting = true;
          this.toggleLoading(true);

          let routeUrl = routes.cart_change_url;
          let body = JSON.stringify({
            quantity,
            id,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: this.getSectionsUrl(),
          });
          let fetchConfigType;

          if (action === this.actions.add) {
            fetchConfigType = 'javascript';
            routeUrl = routes.cart_add_url;
            body = JSON.stringify({
              items: [
                {
                  quantity: parseInt(quantity, 10),
                  id: parseInt(id, 10),
                },
              ],
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: this.getSectionsUrl(),
            });
          }

          fetch(`${routeUrl}`, { ...fetchConfig(fetchConfigType), body })
            .then((response) => response.text())
            .then((state) => {
              const parsedState = JSON.parse(state);

              if (parsedState.description || parsedState.errors) {
                console.error('Cart update error:', parsedState.description || parsedState.errors);
                return;
              }

              this.renderSections(parsedState);
              this.updateRowCartQuantity(parsedState, id);
              this.showAddedFeedback();
              announce(`Added ${quantity} to quote.`);

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: `spn-add-to-quote-${id}`,
                cartData: parsedState,
              });
            })
            .catch((error) => {
              console.error(error);
              announce('Could not add to quote. Try again.');
            })
            .finally(() => {
              this.isRequesting = false;
              this.toggleLoading(false);
              this.button.removeAttribute('aria-busy');
              this.button.classList.remove('is-pending');
            });
        }

        renderSections(parsedState) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) return;

            // Cart drawer empty/populated state
            if (
              sectionElement.parentElement &&
              sectionElement.parentElement.classList.contains('drawer')
            ) {
              parsedState.items.length > 0
                ? sectionElement.parentElement.classList.remove('is-empty')
                : sectionElement.parentElement.classList.add('is-empty');
            }

            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;

            if (elementToReplace && parsedState.sections?.[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });
        }

        getSectionInnerHTML(html, selector) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const el = parsed.querySelector(selector);
          return el ? el.innerHTML : '';
        }

        updateRowCartQuantity(parsedState, id) {
          const currentItem = parsedState.items.find(
            (item) => item.variant_id === parseInt(id, 10)
          );
          const newQty = currentItem ? currentItem.quantity : 0;
          this.row.dataset.cartQty = newQty;
          if (this.qtyInput) {
            this.qtyInput.dataset.cartQuantity = newQty;
            // Reset the qty input to 1 after adding — the input represents "how many to add next",
            // not the running cart total (that's on the row's data-cart-qty).
            this.qtyInput.value = 1;
          }
        }

        showAddedFeedback() {
          if (!this.button) return;
          window.clearTimeout(this._addedTimeout);
          // Cache the original label once, so we can restore it after the confirmation state.
          if (!this._originalLabel) this._originalLabel = this.button.textContent.trim();
          this.button.classList.add('is-added');
          this.button.textContent = '✓ Added';
          this._addedTimeout = window.setTimeout(() => {
            this.button.classList.remove('is-added');
            this.button.textContent = this._originalLabel;
          }, 2000);
          // Flash the header cart bubble so the count change is impossible to miss.
          this.flashCartBubble();
        }

        flashCartBubble() {
          // Try a few common selectors used by Dawn-based themes for the header cart badge.
          const bubble =
            document.querySelector('#cart-icon-bubble .cart-count-bubble') ||
            document.querySelector('.cart-count-bubble') ||
            document.querySelector('[data-cart-count-bubble]');
          if (!bubble) return;
          bubble.classList.remove('spn-bubble-flash');
          // Force reflow so the animation restarts on repeat clicks.
          // eslint-disable-next-line no-unused-expressions
          void bubble.offsetWidth;
          bubble.classList.add('spn-bubble-flash');
          window.clearTimeout(bubble._spnFlashTimeout);
          bubble._spnFlashTimeout = window.setTimeout(
            () => bubble.classList.remove('spn-bubble-flash'),
            1500
          );
        }

        toggleLoading(enable) {
          if (!this.button) return;
          this.button.disabled = !!enable;
          this.button.classList.toggle('is-loading', !!enable);
        }
      }
    );
  }

  // ---------- Swatch lightbox ----------

  const initSwatchLightbox = (root) => {
    const lightbox = root.querySelector('[data-spn-lightbox]');
    if (!lightbox) return;

    const image = lightbox.querySelector('[data-spn-lightbox-image]');
    const closers = lightbox.querySelectorAll('[data-spn-lightbox-close]');
    let lastFocused = null;

    const openLightbox = (src, alt, srcset, sizes) => {
      lastFocused = document.activeElement;
      window.clearTimeout(lightbox._spnCloseTimeout);
      if (srcset) image.setAttribute('srcset', srcset); else image.removeAttribute('srcset');
      if (sizes) image.setAttribute('sizes', sizes); else image.removeAttribute('sizes');
      image.setAttribute('fetchpriority', 'high');
      image.setAttribute('src', src);
      image.setAttribute('alt', alt || '');
      lightbox.removeAttribute('hidden');
      window.requestAnimationFrame(() => lightbox.classList.add('is-open'));
      lightbox.querySelector('.spn__lightbox-close')?.focus();
      document.addEventListener('keydown', onKeydown);
    };

    const closeLightbox = () => {
      lightbox.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      window.clearTimeout(lightbox._spnCloseTimeout);
      lightbox._spnCloseTimeout = window.setTimeout(() => {
        lightbox.setAttribute('hidden', '');
        image.setAttribute('src', '');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.removeAttribute('fetchpriority');
      }, 220);
      if (typeof lastFocused?.focus === 'function') lastFocused.focus();
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') closeLightbox();
    };

    root.querySelectorAll('[data-spn-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const src = swatch.getAttribute('data-swatch-image');
        if (!src) return; // color-only swatches — no image to enlarge
        openLightbox(
          src,
          swatch.getAttribute('data-swatch-title'),
          swatch.getAttribute('data-swatch-srcset'),
          swatch.getAttribute('data-swatch-sizes')
        );
      });
    });

    closers.forEach((closer) => closer.addEventListener('click', closeLightbox));
  };

  // ---------- Table scroll hint ----------
  // Shows the swipe-hand hint under a spec table only while that table
  // actually overflows its wrapper (i.e. horizontal scroll is possible).

  const initScrollHints = (root) => {
    const wraps = root.querySelectorAll('.spn__table-wrap');
    if (!wraps.length) return;

    const update = () => {
      wraps.forEach((wrap) => {
        const hint = wrap.nextElementSibling;
        if (!hint || !hint.matches('[data-spn-scroll-hint]')) return;
        hint.classList.toggle('is-visible', wrap.scrollWidth > wrap.clientWidth + 1);
      });
    };

    update();
    window.addEventListener('resize', debounceFn(update, 200));
  };

  // ---------- Boot ----------

  const init = () => {
    document.querySelectorAll('.single-product__new').forEach((root) => {
      initTabsAndScrollspy(root);
      initSwatchLightbox(root);
      initScrollHints(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
